import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2,
  Image as ImageIcon,
  Video,
  X,
  Eye,
  EyeOff,
  Sparkles,
  Link as LinkIcon,
  Palette,
  Plus,
  Edit2,
  Trash2,
  Megaphone,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import api from '../../services/api';
import RichTextEditor from '../../components/RichTextEditor';
import { sanitizeHtml } from '../../utils/sanitize';

const GRADIENT_OPTIONS = [
  { value: 'from-blue-600 to-purple-700', label: 'Blue to Purple', preview: 'bg-gradient-to-r from-blue-600 to-purple-700' },
  { value: 'from-emerald-500 to-teal-700', label: 'Emerald to Teal', preview: 'bg-gradient-to-r from-emerald-500 to-teal-700' },
  { value: 'from-orange-500 to-red-600', label: 'Orange to Red', preview: 'bg-gradient-to-r from-orange-500 to-red-600' },
  { value: 'from-pink-500 to-purple-600', label: 'Pink to Purple', preview: 'bg-gradient-to-r from-pink-500 to-purple-600' },
  { value: 'from-slate-800 to-slate-900', label: 'Dark Slate', preview: 'bg-gradient-to-r from-slate-800 to-slate-900' },
  { value: 'from-amber-500 to-orange-600', label: 'Amber to Orange', preview: 'bg-gradient-to-r from-amber-500 to-orange-600' },
];

const SplashManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('banners');
  
  // Banner announcements state
  const [banners, setBanners] = useState([]);
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    title: '',
    message: '',
    link_text: '',
    link_url: '',
    background_color: '#1e40af',
    text_color: '#ffffff',
    is_active: true,
    priority: 0
  });
  
  const [settings, setSettings] = useState({
    is_enabled: false,
    title: '',
    subtitle: '',
    description: '',
    media: [],
    cta_text: '',
    cta_link: '',
    background_color: 'from-blue-600 to-purple-700',
    show_once_per_session: true
  });

  // iter306v: multi-splash-post library state.
  const [splashPosts, setSplashPosts] = useState([]);
  const [activePostId, setActivePostId] = useState(null);
  // iter306cc: track which saved post the editor is currently editing
  // — populated when the user clicks "Load" or when the page loads with
  // an active post. `Save Changes` writes back to THIS id so users can
  // edit any saved post, not just the active one.
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingPostName, setEditingPostName] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchBanners();
    fetchSplashPosts();
  }, []);

  const fetchSplashPosts = async () => {
    try {
      const resp = await api.get('/splash/posts');
      const posts = resp.data?.posts || [];
      setSplashPosts(posts);
      const active = posts.find((p) => p.is_active);
      if (active) {
        setActivePostId(active.id);
        // iter306cc: default the editor target to the active post so
        // "Save Changes" edits the active post out of the box.
        if (!editingPostId) {
          setEditingPostId(active.id);
          setEditingPostName(active.name || 'Active Splash');
        }
      }
    } catch (e) {
      console.error('Error fetching splash posts:', e);
    }
  };

  const handleSaveAsNew = async () => {
    const name = window.prompt('Name this splash post:', settings.title || 'Untitled Splash');
    if (!name) return;
    try {
      await api.post(`/splash/posts?name=${encodeURIComponent(name)}&activate=false`, settings);
      toast.success(`Saved "${name}"`);
      fetchSplashPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDuplicate = async (postId) => {
    try {
      await api.post(`/splash/posts/${postId}/duplicate`);
      toast.success('Duplicated');
      fetchSplashPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate failed');
    }
  };

  const handleActivate = async (postId) => {
    try {
      await api.post(`/splash/posts/${postId}/activate`);
      toast.success('Activated — splash is now live');
      await fetchSplashPosts();
      await fetchSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Activate failed');
    }
  };

  // iter306ee: deactivate the active post (hides the splash for all
  // visitors). The activate-button on each row toggles into this
  // for whichever row is currently active.
  const handleDeactivate = async (postId) => {
    if (!window.confirm('Deactivate the active splash? Visitors will no longer see any splash announcement.')) return;
    try {
      await api.post(`/splash/posts/${postId}/deactivate`);
      toast.success('Deactivated — splash is now hidden');
      await fetchSplashPosts();
      await fetchSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Deactivate failed');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this saved splash post?')) return;
    try {
      await api.delete(`/splash/posts/${postId}`);
      toast.success('Deleted');
      fetchSplashPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleLoadPost = (post) => {
    // Load the saved fields into the editor for previewing/editing.
    setSettings({
      is_enabled: post.is_enabled ?? false,
      title: post.title || '',
      subtitle: post.subtitle || '',
      description: post.description || '',
      media: post.media || [],
      cta_text: post.cta_text || '',
      cta_link: post.cta_link || '',
      background_color: post.background_color || 'from-blue-600 to-purple-700',
      show_once_per_session: post.show_once_per_session ?? true,
    });
    // iter306cc: also retarget the editor to THIS post so subsequent
    // Save Changes writes back to it.
    setEditingPostId(post.id);
    setEditingPostName(post.name || 'Untitled Splash');
    toast.success(`Now editing "${post.name}"`);
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/splash');
      setSettings({
        is_enabled: response.data.is_enabled || false,
        title: response.data.title || '',
        subtitle: response.data.subtitle || '',
        description: response.data.description || '',
        media: response.data.media || [],
        cta_text: response.data.cta_text || '',
        cta_link: response.data.cta_link || '',
        background_color: response.data.background_color || 'from-blue-600 to-purple-700',
        show_once_per_session: response.data.show_once_per_session ?? true
      });
    } catch (error) {
      console.error('Error fetching splash settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBanners = async () => {
    try {
      const response = await api.get('/splash/banners');
      setBanners(response.data || []);
    } catch (error) {
      console.error('Error fetching banners:', error);
    }
  };

  const openBannerDialog = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setBannerForm({
        title: banner.title,
        message: banner.message,
        link_text: banner.link_text || '',
        link_url: banner.link_url || '',
        background_color: banner.background_color || '#1e40af',
        text_color: banner.text_color || '#ffffff',
        is_active: banner.is_active,
        priority: banner.priority || 0
      });
    } else {
      setEditingBanner(null);
      setBannerForm({
        title: '',
        message: '',
        link_text: '',
        link_url: '',
        background_color: '#1e40af',
        text_color: '#ffffff',
        is_active: true,
        priority: 0
      });
    }
    setBannerDialogOpen(true);
  };

  const saveBanner = async () => {
    if (!bannerForm.title || !bannerForm.message) {
      toast.error('Title and message are required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingBanner) {
        await api.put(`/splash/banners/${editingBanner.id}`, bannerForm);
        toast.success('Banner updated successfully');
      } else {
        await api.post('/splash/banners', bannerForm);
        toast.success('Banner created successfully');
      }
      setBannerDialogOpen(false);
      fetchBanners();
    } catch (error) {
      toast.error('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const deleteBanner = async (bannerId) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    
    try {
      await api.delete(`/splash/banners/${bannerId}`);
      toast.success('Banner deleted');
      fetchBanners();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const toggleBanner = async (bannerId) => {
    try {
      const response = await api.patch(`/splash/banners/${bannerId}/toggle`);
      toast.success(response.data.message);
      fetchBanners();
    } catch (error) {
      toast.error('Failed to toggle banner');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // iter306cc: write to the SAVED POST the user is currently
      // editing (Load → edit → Save Changes targets THAT row, not just
      // the active one). If we're editing the active post, also push
      // the legacy `/splash` doc for back-compat.
      const targetPostId = editingPostId || activePostId;
      if (targetPostId) {
        await api.put(`/splash/posts/${targetPostId}`, settings);
        // Refresh editing label in case the underlying name changed.
      }
      // Keep the legacy single-doc in sync when we're editing the
      // active post (public GET /splash falls back to it).
      const isActive = targetPostId && targetPostId === activePostId;
      if (isActive || !targetPostId) {
        await api.put('/splash', settings);
      }
      toast.success(`Saved "${editingPostName || 'splash'}"`);
      fetchSplashPosts();
    } catch (error) {
      console.error('Error saving splash:', error);
      toast.error('Failed to save splash announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    try {
      const response = await api.post('/splash/toggle');
      setSettings(prev => ({ ...prev, is_enabled: response.data.is_enabled }));
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to toggle splash announcement');
    }
  };

  const handleMediaUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (type === 'image' && !file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (type === 'video' && !file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/splash/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newMedia = {
        type,
        url: response.data.url,
        thumbnail: type === 'video' ? null : response.data.url
      };

      setSettings(prev => ({
        ...prev,
        media: [...prev.media, newMedia]
      }));
      
      toast.success(`${type === 'image' ? 'Image' : 'Video'} uploaded successfully`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload media');
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = (index) => {
    setSettings(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <DashboardLayout title="Announcements">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Announcements">
      <div className="space-y-6" data-testid="splash-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-amber-500" />
              Announcements
            </h1>
            <p className="text-slate-600">Manage banners and splash announcements for your site</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="banners" data-testid="banners-tab">
              <Megaphone className="h-4 w-4 mr-2" />
              Banner Announcements
            </TabsTrigger>
            <TabsTrigger value="splash" data-testid="splash-tab">
              <Sparkles className="h-4 w-4 mr-2" />
              Full-Screen Splash
            </TabsTrigger>
          </TabsList>

          {/* Banner Announcements Tab */}
          <TabsContent value="banners" className="mt-6 space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Banner Announcements</CardTitle>
                  <CardDescription>
                    Create scrollable banner announcements that appear at the top of your landing page
                  </CardDescription>
                </div>
                <Button onClick={() => openBannerDialog()} data-testid="add-banner-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Banner
                </Button>
              </CardHeader>
              <CardContent>
                {banners.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Megaphone className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>No banner announcements yet</p>
                    <p className="text-sm">Create your first banner to display on the landing page</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {banners.map((banner) => (
                      <div
                        key={banner.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                        style={{ 
                          backgroundColor: banner.is_active ? banner.background_color + '20' : '#f1f5f9',
                          borderColor: banner.is_active ? banner.background_color : '#e2e8f0'
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold truncate">{banner.title}</h4>
                            <Badge variant={banner.is_active ? 'default' : 'secondary'}>
                              {banner.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge variant="outline">Priority: {banner.priority}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 truncate">{banner.message}</p>
                          {banner.link_url && (
                            <p className="text-xs text-blue-600 mt-1">
                              <LinkIcon className="h-3 w-3 inline mr-1" />
                              {banner.link_text || banner.link_url}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <div
                            className="w-6 h-6 rounded border"
                            style={{ backgroundColor: banner.background_color }}
                            title="Background color"
                          />
                          <Switch
                            checked={banner.is_active}
                            onCheckedChange={() => toggleBanner(banner.id)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openBannerDialog(banner)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteBanner(banner.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Preview of active banners */}
                {banners.filter(b => b.is_active).length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Preview (Active Banners)
                    </h4>
                    <BannerPreview banners={banners.filter(b => b.is_active)} />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Full-Screen Splash Tab */}
          <TabsContent value="splash" className="mt-6 space-y-4">
            {/* iter306cc: clear "now editing" banner so users know which
                saved post will be overwritten by Save Changes. */}
            {editingPostName && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-900" data-testid="splash-editing-banner">
                <span className="font-semibold">Now editing:</span>
                <span>{editingPostName}</span>
                {editingPostId === activePostId && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded ml-1">
                    Active
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center justify-end gap-3 mb-4">
              <Button
                variant="outline"
                onClick={() => setShowPreview(true)}
                disabled={!settings.title}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                variant="outline"
                onClick={handleSaveAsNew}
                data-testid="splash-save-as-new"
              >
                Save as New
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </div>

            {/* iter306v: Saved Posts library — list every saved splash
                post with Load / Duplicate / Activate / Delete actions. */}
            {splashPosts.length > 0 && (
              <Card data-testid="splash-posts-library">
                <CardHeader>
                  <CardTitle className="text-base">Saved Splash Posts</CardTitle>
                  <CardDescription>Switch between saved versions. Only one can be active at a time.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {splashPosts.map((post) => (
                      <div
                        key={post.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          post.id === editingPostId
                            ? 'border-blue-500 bg-blue-50'
                            : post.is_active
                              ? 'border-green-500 bg-green-50'
                              : 'border-slate-200 bg-white'
                        }`}
                        data-testid={`splash-post-row-${post.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">{post.name || 'Untitled'}</p>
                            {post.is_active && (
                              <span className="text-[10px] uppercase tracking-wider font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                                Active
                              </span>
                            )}
                            {post.id === editingPostId && (
                              <span className="text-[10px] uppercase tracking-wider font-semibold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                                Editing
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 truncate">{post.title || '—'}</p>
                        </div>
                        <div className="flex items-center gap-1 ml-3">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => handleLoadPost(post)}
                            data-testid={`splash-post-load-${post.id}`}
                          >Edit</Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => handleDuplicate(post.id)}
                            data-testid={`splash-post-duplicate-${post.id}`}
                          >Duplicate</Button>
                          {post.is_active ? (
                            <Button
                              size="sm"
                              onClick={() => handleDeactivate(post.id)}
                              variant="outline"
                              className="border-amber-500 text-amber-700 hover:bg-amber-50"
                              data-testid={`splash-post-deactivate-${post.id}`}
                            >Deactivate</Button>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleActivate(post.id)}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid={`splash-post-activate-${post.id}`}
                              >Activate</Button>
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => handleDeletePost(post.id)}
                                className="text-rose-600 hover:text-rose-700"
                                data-testid={`splash-post-delete-${post.id}`}
                              >Delete</Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

        {/* iter306ee: Status banner replaces the conflicting
            "Splash is Live" toggle. Visibility is now controlled
            EXCLUSIVELY by which post is Active (Activate button on
            each row). Click "Deactivate" on the active row to hide
            the splash entirely. */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${activePostId ? 'bg-green-100' : 'bg-slate-100'}`}>
                {activePostId ? (
                  <Eye className="w-6 h-6 text-green-600" />
                ) : (
                  <EyeOff className="w-6 h-6 text-slate-400" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  {activePostId
                    ? `Splash is Live · "${splashPosts.find(p => p.id === activePostId)?.name || 'Active post'}"`
                    : 'Splash is Hidden'}
                </h3>
                <p className="text-sm text-slate-500">
                  {activePostId
                    ? 'Visitors will see the active splash when they visit your homepage. Click "Deactivate" on the active post below to hide it.'
                    : 'No splash is active. Click "Activate" on a saved post below to make it visible.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Content Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Content</CardTitle>
              <CardDescription>What do you want to announce?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={settings.title}
                  onChange={(e) => setSettings(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Exciting News!"
                  className="mt-1"
                  data-testid="splash-title"
                />
              </div>

              <div>
                <Label>Subtitle</Label>
                <Input
                  value={settings.subtitle}
                  onChange={(e) => setSettings(prev => ({ ...prev, subtitle: e.target.value }))}
                  placeholder="A brief tagline..."
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Description</Label>
                <div className="mt-1">
                  <RichTextEditor
                    value={settings.description}
                    onChange={(value) => setSettings(prev => ({ ...prev, description: value }))}
                    placeholder="Tell your visitors more..."
                    minHeight="120px"
                    maxHeight="200px"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Button Text</Label>
                  <Input
                    value={settings.cta_text}
                    onChange={(e) => setSettings(prev => ({ ...prev, cta_text: e.target.value }))}
                    placeholder="Learn More"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Button Link</Label>
                  <Input
                    value={settings.cta_link}
                    onChange={(e) => setSettings(prev => ({ ...prev, cta_link: e.target.value }))}
                    placeholder="/blog or https://..."
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Switch
                  checked={settings.show_once_per_session}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_once_per_session: checked }))}
                />
                <Label className="text-sm">Only show once per browser session</Label>
              </div>
            </CardContent>
          </Card>

          {/* Media & Style */}
          <div className="space-y-6">
            {/* Media Upload */}
            <Card>
              <CardHeader>
                <CardTitle>Media</CardTitle>
                <CardDescription>Add images or a video to showcase</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Media Preview */}
                {settings.media.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {settings.media.map((m, index) => (
                      <div key={index} className="relative group aspect-video bg-slate-100 rounded-md overflow-hidden">
                        {m.type === 'image' ? (
                          <img src={m.url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <video src={m.url} className="w-full h-full object-cover" />
                        )}
                        <button
                          onClick={() => removeMedia(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <Badge className="absolute bottom-1 left-1 text-xs">
                          {m.type === 'image' ? <ImageIcon className="w-3 h-3 mr-1" /> : <Video className="w-3 h-3 mr-1" />}
                          {m.type}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Buttons */}
                <div className="flex gap-2">
                  <label className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleMediaUpload(e, 'image')}
                      className="hidden"
                      disabled={uploading}
                    />
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={uploading}
                      asChild
                    >
                      <span>
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                        Add Image
                      </span>
                    </Button>
                  </label>
                  <label className="flex-1">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => handleMediaUpload(e, 'video')}
                      className="hidden"
                      disabled={uploading}
                    />
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={uploading}
                      asChild
                    >
                      <span>
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Video className="w-4 h-4 mr-2" />}
                        Add Video
                      </span>
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-slate-500">
                  Add multiple images for a carousel, or one video for auto-play
                </p>
              </CardContent>
            </Card>

            {/* Background Color */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Background Style
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2">
                  {GRADIENT_OPTIONS.map((gradient) => (
                    <button
                      key={gradient.value}
                      onClick={() => setSettings(prev => ({ ...prev, background_color: gradient.value }))}
                      className={`h-12 rounded-md ${gradient.preview} transition-all ${
                        settings.background_color === gradient.value 
                          ? 'ring-2 ring-offset-2 ring-blue-500' 
                          : 'hover:scale-105'
                      }`}
                      title={gradient.label}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setShowPreview(false)}>
            <div 
              className={`relative w-full max-w-4xl max-h-[90vh] overflow-auto rounded-2xl bg-gradient-to-br ${settings.background_color} text-white p-8`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowPreview(false)}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="text-center space-y-4">
                {settings.subtitle && (
                  <p className="text-white/80 text-sm uppercase tracking-wider">{settings.subtitle}</p>
                )}
                <h2 className="text-4xl font-bold">{settings.title || 'Your Title Here'}</h2>
                
                {settings.description && (
                  <div 
                    className="prose prose-invert prose-sm max-w-2xl mx-auto"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(settings.description.replace(/\n/g, '<br>')) }}
                  />
                )}

                {settings.media.length > 0 && (
                  <div className="mt-6">
                    {settings.media[0].type === 'video' ? (
                      <video 
                        src={settings.media[0].url} 
                        className="w-full max-h-80 object-contain rounded-lg mx-auto"
                        controls
                      />
                    ) : (
                      <div className="flex justify-center gap-2 overflow-x-auto py-2">
                        {settings.media.map((m, i) => (
                          <img 
                            key={i} 
                            src={m.url} 
                            alt="" 
                            className="h-48 rounded-lg object-cover"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {settings.cta_text && (
                  <Button 
                    size="lg" 
                    className="mt-6 bg-white text-slate-900 hover:bg-white/90"
                  >
                    {settings.cta_text}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
          </TabsContent>
        </Tabs>

        {/* Banner Create/Edit Dialog */}
        <Dialog open={bannerDialogOpen} onOpenChange={setBannerDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingBanner ? 'Edit Banner' : 'Create Banner'}
              </DialogTitle>
              <DialogDescription>
                Banner announcements appear at the top of the landing page
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={bannerForm.title}
                  onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
                  placeholder="e.g., Summer Registration Open!"
                />
              </div>
              
              <div>
                <Label>Message *</Label>
                <Textarea
                  value={bannerForm.message}
                  onChange={(e) => setBannerForm({ ...bannerForm, message: e.target.value })}
                  placeholder="Brief announcement message..."
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Button Text (Optional)</Label>
                  <Input
                    value={bannerForm.link_text}
                    onChange={(e) => setBannerForm({ ...bannerForm, link_text: e.target.value })}
                    placeholder="e.g., Learn More"
                  />
                </div>
                <div>
                  <Label>Button URL (Optional)</Label>
                  <Input
                    value={bannerForm.link_url}
                    onChange={(e) => setBannerForm({ ...bannerForm, link_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Background Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={bannerForm.background_color}
                      onChange={(e) => setBannerForm({ ...bannerForm, background_color: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={bannerForm.background_color}
                      onChange={(e) => setBannerForm({ ...bannerForm, background_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Text Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={bannerForm.text_color}
                      onChange={(e) => setBannerForm({ ...bannerForm, text_color: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={bannerForm.text_color}
                      onChange={(e) => setBannerForm({ ...bannerForm, text_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority (Higher = Shows First)</Label>
                  <Input
                    type="number"
                    value={bannerForm.priority}
                    onChange={(e) => setBannerForm({ ...bannerForm, priority: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="100"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={bannerForm.is_active}
                    onCheckedChange={(checked) => setBannerForm({ ...bannerForm, is_active: checked })}
                  />
                  <Label>Active</Label>
                </div>
              </div>
              
              {/* Preview */}
              <div className="mt-4 pt-4 border-t">
                <Label className="mb-2 block">Preview</Label>
                <div
                  className="p-3 rounded-lg text-center"
                  style={{ 
                    backgroundColor: bannerForm.background_color,
                    color: bannerForm.text_color 
                  }}
                >
                  <span className="font-semibold">{bannerForm.title || 'Title'}</span>
                  {bannerForm.message && <span className="mx-2">—</span>}
                  <span>{bannerForm.message || 'Message'}</span>
                  {bannerForm.link_text && (
                    <button className="ml-3 px-3 py-1 bg-white/20 rounded hover:bg-white/30 text-sm font-medium">
                      {bannerForm.link_text}
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setBannerDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={saveBanner} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingBanner ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Banner Preview Component with carousel
const BannerPreview = ({ banners }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextBanner = () => {
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  if (banners.length === 0) return null;

  const banner = banners[currentIndex];

  return (
    <div className="relative">
      <div
        className="p-3 rounded-lg text-center transition-all duration-300"
        style={{ 
          backgroundColor: banner.background_color,
          color: banner.text_color 
        }}
      >
        <div className="flex items-center justify-center gap-2">
          <span className="font-semibold">{banner.title}</span>
          <span>—</span>
          <span>{banner.message}</span>
          {banner.link_text && banner.link_url && (
            <a
              href={banner.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-3 px-3 py-1 bg-white/20 rounded hover:bg-white/30 text-sm font-medium inline-flex items-center gap-1"
            >
              {banner.link_text}
              <LinkIcon className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
      
      {banners.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={prevBanner}
            className="h-6 w-6 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-slate-500">
            {currentIndex + 1} / {banners.length}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={nextBanner}
            className="h-6 w-6 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default SplashManagement;
